/**
 * Roundtable page (§7h).
 *
 * Mounted at three routes:
 *   /projects/:projectId/roundtable                    — new roundtable, project context
 *   /projects/:projectId/roundtable/:conversationId    — existing roundtable, project context
 *   /coaches/roundtable                                — new roundtable, standalone
 *   /coaches/roundtable/:conversationId                — existing roundtable, standalone
 *
 * State machine:
 *   - `conversationId` in URL → hydrate from GET /api/conversations/:id
 *     via store.hydrateActiveRoundtable, then render the thread with the
 *     follow-up input enabled
 *   - No `conversationId` but `roundtablePanel` is set in store →
 *     render the "new roundtable" empty state (panel avatars at top,
 *     first-message input), waiting for the user to send
 *   - Neither → open the PanelPicker automatically so the page is
 *     recoverable after a refresh (store state is in-memory only)
 *
 * The streaming lifecycle is owned by this page's handleSend:
 *   1. initActiveRoundtable with the user message + panel
 *   2. streamRoundtable() fires SSE events into the store's roundtable
 *      action set (setRoundtableConversationId, startRoundtableSpeaker,
 *      appendRoundtableToken, appendRoundtablePanelError,
 *      finishRoundtableTurn)
 *   3. On roundtable_started the URL is rewritten to include the new
 *      conversationId (replace: true) so a refresh preserves state
 *   4. On roundtable_done the turn finishes; the thread stays rendered
 *      ready for a follow-up
 *
 * The thread + message component + target picker arrive in Commit C/D.
 * This commit lands the page shell with the streaming lifecycle wired
 * up but a placeholder thread body.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header.jsx";
import PanelPicker from "../components/PanelPicker.jsx";
import EmptyState from "../components/EmptyState.jsx";
import ErrorBubble from "../components/ErrorBubble.jsx";
import BudgetFooter from "../components/BudgetFooter.jsx";
import RoundtableThread from "../components/RoundtableThread.jsx";
import TargetPicker from "../components/TargetPicker.jsx";
import { useStore } from "../store/useStore.js";
import { useChatNavigation } from "../hooks/useChatNavigation.js";
import { streamRoundtable } from "../services/roundtable.js";
import * as api from "../services/api.js";

// Module-scoped "just created" conversation id. Must survive the
// component instance remount that happens when the URL flips from
// /projects/:id/roundtable → /projects/:id/roundtable/:convId after
// the first turn (distinct Route elements in App.jsx, so React Router
// unmounts the first and mounts the second). A useRef would die with
// the old instance, triggering a spurious re-fetch of the conversation
// we just finished streaming and flashing an empty thread in the gap.
// This module-level value persists because the file module is loaded
// once per session.
let justCreatedConversationId = null;

export default function Roundtable() {
  const { projectId, conversationId } = useParams();
  const navigate = useNavigate();
  const { roundtablePath, scopeRoot } = useChatNavigation(projectId);

  const panel = useStore((s) => s.roundtablePanel);
  const active = useStore((s) => s.activeRoundtable);
  const hydrateActiveRoundtable = useStore((s) => s.hydrateActiveRoundtable);
  const initActiveRoundtable = useStore((s) => s.initActiveRoundtable);
  const setRoundtableConversationId = useStore(
    (s) => s.setRoundtableConversationId,
  );
  const startRoundtableSpeaker = useStore((s) => s.startRoundtableSpeaker);
  const appendRoundtableToken = useStore((s) => s.appendRoundtableToken);
  const appendRoundtablePanelError = useStore(
    (s) => s.appendRoundtablePanelError,
  );
  const appendRoundtableUserMessage = useStore(
    (s) => s.appendRoundtableUserMessage,
  );
  const finishRoundtableTurn = useStore((s) => s.finishRoundtableTurn);
  const clearActiveRoundtable = useStore((s) => s.clearActiveRoundtable);
  const personas = useStore((s) => s.personas);
  const loadPersonas = useStore((s) => s.loadPersonas);

  const [input, setInput] = useState("");
  const [error, setError] = useState(null);
  const [errorVariant, setErrorVariant] = useState("error");
  // Follow-up targeting: null = ask panel (all panellists respond in
  // order); a personaId = only that panellist responds. Reset to null
  // after every successful send so the "ask panel" default holds.
  const [targetPersonaId, setTargetPersonaId] = useState(null);
  // Derive the initial picker state from store state at mount time
  // rather than from subscribed selectors inside a useEffect. React
  // 19's react-hooks/set-state-in-effect rule forbids unconditional
  // setState inside an effect body because it causes cascading
  // renders. Reading the store synchronously via getState() inside
  // the lazy initializer gives us the same outcome without the
  // re-render loop: if the user lands here with no conversation and
  // no pre-picked panel (e.g. direct URL, page refresh), the picker
  // opens on first paint.
  const [pickerOpen, setPickerOpen] = useState(() => {
    if (conversationId) return false;
    const s = useStore.getState();
    return s.roundtablePanel.length === 0 && !s.activeRoundtable;
  });
  const streamAbortRef = useRef(null);

  // Load personas for name lookups in the thread.
  useEffect(() => {
    loadPersonas();
  }, [loadPersonas]);

  // Single hydrate / clear effect — runs on mount and whenever the
  // URL's conversationId changes. Deliberately NOT an unmount cleanup:
  //
  //   - React Router remounts the Roundtable component on URL flips
  //     between the /roundtable and /roundtable/:convId routes
  //     (distinct Route elements in App.jsx). An unmount cleanup that
  //     cleared `activeRoundtable` and `roundtablePanel` would wipe
  //     the in-flight streaming state during the gap between the two
  //     mounts, causing an "empty thread → loading → full thread"
  //     flicker right after the first turn completes.
  //   - React 19 StrictMode double-invokes effect setup/cleanup in
  //     development. The aggressive cleanup would also erase the
  //     user's picked panel between the two StrictMode passes.
  //
  // Instead: clear state as part of mount/transition logic, guarded
  // against clobbering an in-flight stream. The module-scoped
  // `justCreatedConversationId` above survives remounts so the
  // post-first-turn URL flip can skip the spurious refetch.
  useEffect(() => {
    if (!conversationId) {
      // New-roundtable mode. Only clear stale state if nothing is
      // actively streaming — a stream started by handleSend just
      // before the URL flips must not be wiped out.
      if (!useStore.getState().activeRoundtable?.isStreaming) {
        clearActiveRoundtable();
      }
      return;
    }

    // Existing-conversation mode. If we just created this conv via a
    // first-turn handoff, skip the re-fetch — the store already holds
    // the streamed state from the turn that just completed.
    if (conversationId === justCreatedConversationId) {
      justCreatedConversationId = null;
      return;
    }

    let cancelled = false;
    api
      .getConversation(conversationId)
      .then((conv) => {
        if (cancelled) return;
        hydrateActiveRoundtable(conv);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, clearActiveRoundtable, hydrateActiveRoundtable]);

  // Abort any in-flight stream on unmount. This cleanup IS correct
  // on unmount because the AbortController is component-scoped and
  // can't be used across instances — the new mount will create its
  // own controller on the next handleSend.
  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort();
    };
  }, []);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || useStore.getState().activeRoundtable?.isStreaming) return;

    setError(null);
    setErrorVariant("error");
    setInput("");

    const controller = new AbortController();
    streamAbortRef.current = controller;

    const isFollowUp = !!conversationId;

    if (isFollowUp) {
      appendRoundtableUserMessage(text);
    } else {
      // Kick off a new turn: seed the active state with the panel +
      // first user message before the stream starts firing events.
      initActiveRoundtable({
        conversationId: null,
        personaIds: panel,
        userMessage: text,
      });
    }

    // Capture and reset the target so a subsequent send defaults
    // back to "ask panel". Stash in a local so the in-flight request
    // still uses the captured value even if React batches the reset.
    const capturedTarget = targetPersonaId;
    setTargetPersonaId(null);

    await streamRoundtable({
      personaIds: panel,
      projectId,
      conversationId,
      message: text,
      targetPersonaId: isFollowUp ? capturedTarget : undefined,
      isFollowUp,
      signal: controller.signal,
      onStarted: ({ conversationId: newId }) => {
        if (!newId || newId === conversationId) return;
        // Seed the URL with the new conversationId so a refresh
        // preserves state. Sets the module-scoped guard so the
        // remount's hydrate effect skips the spurious refetch —
        // see the justCreatedConversationId declaration at the
        // top of this file for rationale.
        setRoundtableConversationId(newId);
        justCreatedConversationId = newId;
        navigate(roundtablePath(newId), { replace: true });
      },
      onSpeaker: ({ personaId, index }) => {
        startRoundtableSpeaker({ personaId, index });
      },
      onToken: (personaId, token) => {
        appendRoundtableToken(personaId, token);
      },
      onPanelError: ({ personaId, error: errMsg }) => {
        appendRoundtablePanelError({ personaId, error: errMsg });
      },
      onBudgetExhausted: (err) => {
        finishRoundtableTurn({ budgetExhausted: true });
        setError(err.message);
        setErrorVariant("budget");

      },
      onDone: ({ budgetExhausted }) => {
        finishRoundtableTurn({ budgetExhausted });

      },
      onError: (err) => {
        finishRoundtableTurn();
        setError(err.message);
        setErrorVariant("error");

      },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // RECOVERY state: the hydrate fetch failed (404, network error on a
  // shared/bookmarked URL) and there's no active roundtable to fall
  // back on. Without this branch the page would render with empty
  // panelIds and a disabled send — stranding the user with no way
  // out. Offer a back link to either the project or the coaches grid.
  if (conversationId && error && !active) {
    const backTo = scopeRoot;
    const backLabel = projectId ? "Back to project" : "Back to coaches";
    return (
      <>
        <Header />
        <main className="roundtable-empty">
          <div className="container">
            <EmptyState
              title="Roundtable not found"
              description={error}
              action={
                <Link to={backTo} className="btn btn-primary">
                  {backLabel}
                </Link>
              }
            />
          </div>
        </main>
      </>
    );
  }

  // PICKER-ONLY state: no conversation, no panel, no active turn.
  if (!conversationId && panel.length === 0 && !active) {
    return (
      <>
        <Header />
        <main className="roundtable-empty">
          <div className="container">
            <EmptyState
              title="No roundtable in progress"
              description="Start a new roundtable to debate a question with 2–3 of your coaches."
              action={
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setPickerOpen(true)}
                >
                  Start a roundtable
                </button>
              }
            />
          </div>
        </main>
        <PanelPicker
          open={pickerOpen}
          projectId={projectId || null}
          onClose={() => setPickerOpen(false)}
        />
      </>
    );
  }

  // ACTIVE state: thread + input.
  const panelIds = active?.personaIds || panel;
  const panelPersonas = panelIds
    .map((id) => personas.find((p) => p.id === id))
    .filter(Boolean);

  const isStreaming = active?.isStreaming ?? false;

  return (
    <>
      <Header />
      <main className="roundtable-page">
        <div className="container">
          <div className="roundtable-header">
            <h2>Roundtable</h2>
            <div className="roundtable-panel-strip">
              {panelPersonas.length === 0
                ? panelIds.map((id) => (
                    <span key={id} className="roundtable-panel-chip-empty">
                      {id}
                    </span>
                  ))
                : panelPersonas.map((p, i) => (
                    <span
                      key={p.id}
                      className={`roundtable-panel-chip roundtable-panel-chip-${i + 1}`}
                    >
                      <span className="roundtable-panel-chip-order">{i + 1}</span>
                      {p.name}
                    </span>
                  ))}
            </div>
          </div>

          <RoundtableThread
            messages={active?.messages || []}
            personaIds={panelIds}
            personas={personas}
          />
          {error && <ErrorBubble message={error} variant={errorVariant} />}

          <div className="roundtable-input">
            {/* Target picker only on follow-ups — the first turn
                always goes to the whole panel. */}
            {conversationId && panelIds.length > 0 && (
              <TargetPicker
                personaIds={panelIds}
                personas={personas}
                targetPersonaId={targetPersonaId}
                onChange={setTargetPersonaId}
                disabled={isStreaming}
              />
            )}
            <textarea
              rows={2}
              placeholder={
                targetPersonaId
                  ? `Ask ${
                      personas.find((p) => p.id === targetPersonaId)?.name ||
                      targetPersonaId
                    } a question…`
                  : "Ask the panel a question…"
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSend}
              disabled={isStreaming || !input.trim() || panelIds.length === 0}
            >
              {isStreaming ? "Streaming…" : "Send"}
            </button>
            <BudgetFooter />
          </div>
        </div>
      </main>
    </>
  );
}
